import { domains } from 'lib/config'
import { createClient } from 'lib/Api'
import { AxiosPromise } from 'axios'
import {
  AssignNetSuiteAssetRequest,
  DomainProviderRequestParameters,
  ExternalSiteMapping,
  MergeSiteParameters,
  NetSuiteAsset,
  NetSuiteAssetFilters,
  NetSuiteCustomer,
  NetSuiteCustomerFilters,
  PatchResponse,
  ProductUserRequestParameters,
  ProfileSearchFilters,
  SendEmailRequest,
  Site,
  SiteFilters,
  SiteUpdate,
  SiteUser,
} from 'types'

/**
 * Second Step API Client Architecture:
 *
 * This file demonstrates the pattern for creating API clients that:
 * 1. Use the base API client from lib/Api.js (which handles authentication - shared across all instances that use it)
 * 2. Point to specific API versions (v3, v4, etc.)
 * 3. Provide typed methods for specific endpoints
 * 4. Support custom API addresses for different environments
 *

 * To create a new API version client:
 * 1. Create a new file (e.g., secondstepApiV5.ts)
 * 2. Change the baseApiAddress to point to the desired version
 * 3. Add/remove methods as needed for that version
 */

const baseApiAddress = `${domains.SS_API_V4}`

// Utility function to build query string from params object
const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams()
  Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Handle arrays by adding multiple parameters with the same key
        value.forEach(item => searchParams.append(key, String(item)))
      } else {
        searchParams.append(key, String(value))
      }
    })

  return searchParams.toString()
}

// We need to set auth on this specific client instance
export const _ssapi = (customApiAddress?: string) => {
  const _api = createClient(customApiAddress || baseApiAddress)

  return {
    //#region Domain Providers
    postDomainProvider: (parameters: DomainProviderRequestParameters) =>
      _api.post(`/domainprovider`, parameters),
    //#endregion

    //#region NetSuite Customers
    getNetSuiteCustomer: (id: number): AxiosPromise<NetSuiteCustomer> => {
      return _api.get(`/netsuitecustomers/${id}`)
    },
    getNetSuiteCustomers: (
      params: NetSuiteCustomerFilters,
    ): AxiosPromise<NetSuiteCustomer[]> => {
      const queryString = buildQueryString(params)
      return _api.get(`/netsuitecustomers?${queryString}`)
    },
    //#endregion

    //#region NetSuite Assets
    getNetSuiteAssets: (
      params: NetSuiteAssetFilters,
    ): AxiosPromise<NetSuiteAsset[]> => {
      const queryString = buildQueryString(params)
      return _api.get(`/netsuiteassets?${queryString}`)
    },
    getNetSuiteAssetById: (assetId: number): AxiosPromise<NetSuiteAsset> =>
      _api.get(`/netsuiteassets/${assetId}`),

    updateNetSuiteAsset: (assetId: number, asset: AssignNetSuiteAssetRequest) =>
      _api.put(`/netsuiteassets/${assetId}`, asset),
    //#endregion

    //#region Pacing
    patchPacing: (bodyFormData: FormData) =>
      _api.patch(`/sites/pacing`, bodyFormData),
    //#endregion

    //#region BulkAssignNetSuiteAssets
    patchNetSuiteAssets: (bodyFormData: FormData) =>
      _api.patch(`/netsuiteassets`, bodyFormData),
    //#endregion

    //#region Profile
    deleteUser: (userEmail: string) =>
      _api.delete(`/profile/deleteUser/${encodeURIComponent(userEmail)}`),

    getArchetypes: () => _api.get('/profile/archetypes'),

    getSetupAdminUserId: (setupAdminEmail: string) =>
      _api.get(`/profile/search?email=${setupAdminEmail}`),

    getUserDetails: (userId: number) =>
      _api.get(`/profile/details?userId=${userId}`),

    getUserProfile: () => _api.get('/profile/me'),

    saveProductUser: (productUser: ProductUserRequestParameters) =>
      _api.put('/profile', productUser), // TODO: Double check these parameters for correctness

    searchProfiles: (searchCriteria: ProfileSearchFilters) => {
      const queryString = buildQueryString(searchCriteria)
      return _api.get(`/profile/search?${queryString}`)
    },

    addPakToProductUser: (pakNumber: string, userId: number) =>
      _api.post('/userpak', {
        PakNumber: pakNumber,
        UserId: userId,
      }),

    removePakFromProductUser: (userEmail: string, pakNo: string) =>
      _api.delete(
        `/admin/user/${encodeURIComponent(userEmail)}/pak/${encodeURIComponent(
          pakNo,
        )}`,
      ),
    //#endregion

    //#region Sites
    bulkUpdateSitePreferences: (bodyFormData: FormData) =>
      _api.patch(`/sites/bulkpreferences`, bodyFormData),

    getSite: (siteId: number): AxiosPromise<Site> =>
      _api.get(`/sites/${siteId}`),

    getSites: (params: SiteFilters): AxiosPromise<Site[]> => {
      const queryString = buildQueryString(params)
      return _api.get(`/sites?${queryString}`)
    },

    mergeSite: (params: MergeSiteParameters) => {
      const { siteId, primarySiteId, isPreview } = params
      return _api.put(`/sites/${siteId}/move`, {
        primarySiteId,
        isPreview,
      })
    },

    siteOrgNetSuiteCustomerSearch: (nsCustomerId: string, orgId: number) =>
      _api.get(`/sites?NSCustomerId=${nsCustomerId}&OrganizationId=${orgId}`),

    updateSite: (site: SiteUpdate) => _api.put(`/sites/${site.id}`, site),
    //#endregion

    //#region Site Users
    bulkCreateSiteUsers: (
      siteId: number,
      siteUsers: SiteUser[],
      sendEmail: boolean = true,
    ) =>
      _api.patch(
        `/sites/${siteId}/siteUsers?sendEmail=${sendEmail}`,
        siteUsers,
      ),

    createSiteUser: (siteUser: SiteUser, sendEmail: boolean = true) =>
      _api.post(
        `/sites/${siteUser.siteId}/siteUsers?sendEmail=${sendEmail}`,
        siteUser,
      ),

    getSiteUsers: (siteId: number) => _api.get(`/sites/${siteId}/siteUsers`),

    remindSiteUser: (siteUser: SiteUser) =>
      _api.post(`/sites/${siteUser.siteId}/siteusers/${siteUser.id}/remind`),

    remindSiteUsers: (siteId: number, emailFilters: SendEmailRequest) =>
      _api.post(`/sites/${siteId}/siteusers/remind`, emailFilters),

    updateSiteUser: (siteUser: SiteUser) =>
      _api.put(`/sites/${siteUser.siteId}/siteUsers/${siteUser.id}`, siteUser),

    //#endregion

    //#region External Site Mapping
    bulkAddExternalSiteMapping: (
      bodyFormData: FormData,
    ): AxiosPromise<PatchResponse<ExternalSiteMapping>> =>
      _api.patch(`/sites/externalsitemapping`, bodyFormData),
    //#endregion

    //#region Location
    getCountries: (): AxiosPromise<string[]> => _api.get('/location/countries'),

    getRegions: (country: string): AxiosPromise<string[]> =>
      _api.get(`/location/regions?country=${country}`),

    getCities: (country: string, region?: string): AxiosPromise<string[]> => {
      const params: Record<string, string> = { country }
      if (region) {
        params.region = region
      }
      const queryString = buildQueryString(params)
      return _api.get(`/location/cities?${queryString}`)
    },

    //#endregion
  }
}
const ssapi = _ssapi()
export default ssapi
